import IconButton from '@material-ui/core/IconButton';
import InputBase from '@material-ui/core/InputBase';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import MinusIcon from '@material-ui/icons/RemoveCircleOutline';
import withStyles from '@material-ui/styles/withStyles';
import * as _ from 'lodash';
import moment from 'moment';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import Autocomplete from 'UI/autocomplete';
import Button from 'UI/button';
import DateRangeSelection from 'UI/dateRangeSelection';
import Footer from 'UI/footer';
import Loader from 'UI/loader';
import { baseURL } from 'utils/apiEndpoints';
import { postRequest } from 'utils/apiRequests';
import { getValidDates } from 'utils/common';

class RoutePlan extends Component {
  constructor(props) {
    super(props);
    const {
      requirement,
      routes = [],
    } = this.props;
    this.disabledDateFn = undefined;
    const { tripDates: { from, to } } = requirement;
    if (from && to) {
      this.disabledDateFn = getValidDates(from, to);
    }
    this.state = {
      errorMsg: '',
      isLoadingSuggestions: false,
      disableCta: false,
      isLoadingSummary: false,
      summaryFromGetRouteSummary: false,
    };
    if (routes.length === 0) {
      this.handleItemAddition(true);
    }
    // Debounce the getRouteSummary call to avoid excessive API requests
    this.debouncedGetRouteSummary = _.debounce((routes) => {
      this.getRouteSummary(routes);
    }, 5000);
  }

  componentDidMount() {
    console.log('componentDidMount ');
    const { routes = [], cities = [] } = this.props;
    if (_.isEmpty(routes)) {
      console.log('componentDidMount empty routes');
      this.getRouteSuggestions({});
    }
    else if (_.isEmpty(cities)) {
      console.log('componentDidMount empty cities');
      this.getRouteSuggestions({
        onlyCities: !_.isEmpty(routes),
      });
    }
  }

  componentDidUpdate(prevProps) {
    console.log('componentDidUpdate ');
    // Check if requirement data changed and we need to refetch
    const { routes } = this.props;
    const { isLoadingSuggestions } = this.state;
    if (!_.isEqual(prevProps.routes, routes)) {
      const { error, errorMsg } = this.validateRoutes(routes);
      if (error) {
        this.setState({ errorMsg });
      }
    }

    if (_.isEmpty(routes) && !isLoadingSuggestions) {
      console.log('componentDidUpdate empty routes');
      this.getRouteSuggestions({});
    }
  }

  buildRequirementObject = () => {
    const { requirement } = this.props;
    const {
      origin = { items: [] }, places = { items: [] }, tripDates: { from, to } = {}, isFlexible = false,
      roomings = [], tripTypes = { items: [] }, duration = 0, flightsBooked = false,
      tags = [], templateItineraryId = null, clientPreferences = '',
      flightArrivalDateTime = null, flightDepartureDateTime = null,
      hotelCategory = '', paceOfTrip = '', foodPreference = { items: [] }, occasions = { items: [] }, mealPlan = { items: [] }, transferMode = { items: [] },
      totalBudget = '', customBudget = '',
      visaAddon = false, cruiseAddon = false, splitStay = false, cruiseName = '', cruiseNights = null,
    } = requirement;

    return {
      origin: (origin.items || []).map((o) => o._id),
      places: (places.items || []).map((d) => d._id),
      departureDate: from ? moment(from).toISOString() : null,
      arrivalDate: to ? moment(to).toISOString() : null,
      duration,
      travellers: {
        adults: _.sumBy(roomings || [], (r) => r.adults || 0),
        children: _.sumBy(roomings || [], (r) => r.children || 0),
        infants: _.sumBy(roomings || [], (r) => r.infants || 0),
      },
      roomings: roomings || [],
      tripTypes: (tripTypes.items || []).map((t) => t.code),
      flightsBooked,
      flexible: isFlexible,
      tags: tags || [],
      clientPreferences: (clientPreferences || '').trim(),
      templateItineraryId: templateItineraryId || null,
      flightArrivalDateTime: _.isNil(flightArrivalDateTime) ? null : moment(flightArrivalDateTime),
      flightDepartureDateTime: _.isNil(flightDepartureDateTime) ? null : moment(flightDepartureDateTime),
      hotelCategory: hotelCategory || '',
      paceOfTrip: paceOfTrip || '',
      foodPreference: (foodPreference.items || []).map((f) => f.code),
      occasions: (occasions.items || []).map((o) => o.code),
      mealPlan: (mealPlan.items || []).map((m) => m.code),
      transferMode: (transferMode.items || []).map((t) => t.code),
      totalBudget: totalBudget === 'Enter Custom' ? (customBudget || '').trim() : (totalBudget || '').trim(),
      addOns: _.compact([
        visaAddon ? 'visa' : null,
        cruiseAddon ? 'cruise' : null,
      ]),
      cruiseName: cruiseName || '',
      cruiseNights: _.isNil(cruiseNights) || cruiseNights === '' ? null : Number(String(cruiseNights).split(' ')[0]),
      splitStay: splitStay || false,
    };
  };

  getRouteSuggestions = async ({ onlyCities = false }) => {
    const { onUpdate } = this.props;
    const requirement = this.buildRequirementObject();
    this.setState({ isLoadingSuggestions: true });
    
    const maxRetries = 3;
    let lastError = null;
    let lastResponse = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await postRequest(
          `${baseURL}itineraries/getRouteSuggestion`,
          {
            requirement,
            onlyCities,
          }
        );
        
        if (response && !response.error) {
          const { summary = '', routes: apiRoutes } = response;
          const cities = response.cities?.map((c) => {
            const cCopy = { ...c };
            cCopy.displayName = `${cCopy.name} ( ${cCopy.countryFlag} ${cCopy.countryName} )`;
            return cCopy;
          }) || [];

          const { routes, onUpdate } = this.props;
          this.setState({
            isLoadingSuggestions: false,
          });
          
          // Update summary in parent
                  
          // Populate routes from API response if routes are empty
          if (!_.isEmpty(apiRoutes)) {
            const formattedRoutes = apiRoutes.map((route) => {
              const city = cities.find((c) => c._id === route.city);
              return {
                placeId: route.city,
                placeName: city?.name || '',
                displayName: city?.displayName || '',
                startDate: moment(route.startDate).toISOString(),
                endDate: moment(route.endDate).toISOString(),
                nights: route.nights,
              };
            });
            console.log('setting routes from formattedRoutes');
            onUpdate({ field: 'routes', value: formattedRoutes, resetRoutes: false, noEdits: true });
            onUpdate({ field: 'summary', value: summary, noEdits: true, resetRoutes: false, noEdits: true });
          }
          else if (!_.isEmpty(routes)) {
            const formattedRoutes = routes.map((route) => {
              const city = cities.find((c) => c._id === route.placeId);
              return {
                ...route,
                placeName: city?.name || route.placeName,
                displayName: city?.displayName || route.displayName,
              };
            });
            console.log('setting routes from routes');
            onUpdate({ field: 'routes', value: formattedRoutes, resetRoutes: false, noEdits: true });
          }

          onUpdate({ field: 'cities', value: cities, noEdits: true, resetRoutes: false, noEdits: true });
          // Summary from getRouteSuggestions, so set flag to false
          this.setState({ summaryFromGetRouteSummary: false });
          return; // Success, exit retry loop
        } else {
          // API returned an error response
          lastResponse = response;
          if (attempt < maxRetries) {
            // Wait before retrying (exponential backoff: 1s, 2s, 4s)
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
            continue;
          }
        }
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff: 1s, 2s, 4s)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
          continue;
        }
      }
    }
    
    // All retries exhausted, handle error
    this.setState({
      errorMsg: lastResponse?.message || lastError?.message || 'Failed to fetch route suggestions after multiple attempts',
      isLoadingSuggestions: false,
    });
  };

  getRouteSummary = async (routesToUse = null) => {
    const { routes: propsRoutes, onUpdate } = this.props;
    // Use provided routes or fall back to props
    const routes = routesToUse || propsRoutes;
    
    // Don't call API if routes are empty or invalid
    if (!routes || routes.length === 0) {
      return;
    }

    // Check if routes have at least one valid place
    const hasValidPlace = routes.some((route) => route.placeId || route.placeName);
    if (!hasValidPlace) {
      return;
    }

    const requirement = this.buildRequirementObject();
    
    // Format routes for API
    const formattedRoutes = routes.map((route) => ({
      displayName: route.displayName,
      startDate: route.startDate,
      endDate: route.endDate,
      nights: route.nights,
    }));

    this.setState({ isLoadingSummary: true });
    try {
      const response = await postRequest(
        `${baseURL}itineraries/getRouteSummary`,
        {
          requirement,
          routes: formattedRoutes,
        }
      );
      if (response && !response.error) {
        console.log('getRouteSummary response', response);
        const { summary = '' } = response;
        this.setState({ summary, summaryFromGetRouteSummary: true });
        onUpdate({ field: 'summary', value: summary, noEdits: true, resetRoutes: false });
        this.setState({ isLoadingSummary: false, disableCta: false });
      } else {
        this.setState({ isLoadingSummary: false, disableCta: false });
      }
    } catch (error) {
      console.error('Error fetching route summary:', error);
      this.setState({ isLoadingSummary: false, disableCta: false });
    }
  };

  handleItemAddition = (initial) => {
    const { routes, onUpdate, requirement } = this.props;
    const { tripDates: { from } = {}, places = { items: [] } } = requirement;
    const modRoutes = [...routes];
    let finalFrom = from;
    if (routes.length) {
      const lastRoute = routes[routes.length - 1];
      finalFrom = lastRoute.endDate;
    }
    const startDate = moment(finalFrom).toISOString();
    const endDate = moment(finalFrom).add(1, 'day').toISOString();
    modRoutes.push({
      placeId: '',
      placeName: '',
      displayName: '',
      startDate,
      endDate,
      nights: 1,
    });
    if (initial && modRoutes.length === 1 && places.items && places.items[0]) {
      modRoutes[0].placeId = places.items[0]._id || '';
      modRoutes[0].placeName = places.items[0].name || '';
      modRoutes[0].displayName = places.items[0].displayName || '';
    }
    const { error, errorMsg } = this.validateRoutes(modRoutes);
    if (error) {
      this.setState({ errorMsg: errorMsg });
      return;
    }
    onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
    // Call getRouteSummary after route addition
    if (!initial) {
      this.setState({ disableCta: true });
      this.debouncedGetRouteSummary(modRoutes);
    }
  };

  handleDateSelection = (index, dateRange) => {
    const { routes, onUpdate } = this.props;
    const modRoutes = [...routes];
    const route = { ...routes[index] };
    const nights = (moment(dateRange.to).unix() - moment(dateRange.from).unix()) / 86400;
    route.startDate = moment(dateRange.from).toISOString();
    route.endDate = moment(dateRange.to).toISOString();
    route.nights = nights;
    modRoutes[index] = route;

    // Handle date overlap adjustments
    this.adjustOverlappingDates(modRoutes, index);

    const { error, errorMsg } = this.validateRoutes(modRoutes);
    if (error) {
      this.setState({ errorMsg: errorMsg });
      return;
    }
    onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
    // Call getRouteSummary after date selection
    this.setState({ disableCta: true });
    this.debouncedGetRouteSummary(modRoutes);
  };

  adjustOverlappingDates = (routes, index) => {
    /* eslint-disable no-param-reassign */
    // The route at index is sacrosanct - we only adjust its neighbors
    const route = routes[index];
    const routeStart = moment(route.startDate);
    const routeEnd = moment(route.endDate);
    const { requirement } = this.props;
    const tripStart = requirement.tripDates?.from ? moment(requirement.tripDates.from) : null;
    const tripEnd = requirement.tripDates?.to ? moment(requirement.tripDates.to) : null;

    // Adjust previous route: endDate must equal current route's startDate
    if (index > 0) {
      const prevRoute = routes[index - 1];
      const prevEnd = moment(prevRoute.endDate);
      const prevStart = moment(prevRoute.startDate);

      // Desired boundary: previous route's endDate should equal current route's startDate
      const desiredPrevEnd = routeStart;

      if (!prevEnd.isSame(desiredPrevEnd, 'day')) {
        // Adjust previous route's end date to match current route's start date
        const newPrevEnd = desiredPrevEnd;
        const newPrevStart = moment(newPrevEnd).subtract(prevRoute.nights, 'days');

        // Respect trip start boundary
        const finalPrevStart = tripStart && newPrevStart.isBefore(tripStart, 'day')
          ? tripStart
          : newPrevStart;
        const finalPrevEnd = tripStart && newPrevStart.isBefore(tripStart, 'day')
          ? moment.min(moment(finalPrevStart).add(prevRoute.nights, 'days'), desiredPrevEnd)
          : newPrevEnd;

        routes[index - 1].startDate = finalPrevStart.toISOString();
        routes[index - 1].endDate = finalPrevEnd.toISOString();
        routes[index - 1].nights = moment(finalPrevEnd).diff(moment(finalPrevStart), 'days');

        // Ensure minimum 1 night
        if (routes[index - 1].nights < 1) {
          routes[index - 1].nights = 1;
          routes[index - 1].endDate = moment(routes[index - 1].startDate).add(1, 'day').toISOString();
        }
      }
    }

    // Adjust next route: startDate must equal current route's endDate
    if (index < routes.length - 1) {
      const nextRoute = routes[index + 1];
      const nextStart = moment(nextRoute.startDate);
      const nextEnd = moment(nextRoute.endDate);

      // Desired boundary: next route's startDate should equal current route's endDate
      const desiredNextStart = routeEnd;

      if (!nextStart.isSame(desiredNextStart, 'day')) {
        // Adjust next route's start date to match current route's end date
        const newNextStart = desiredNextStart;

        // Calculate maximum end date: trip end or next route boundary
        let maxEnd = tripEnd || null;
        // Check if there's a route after this one
        if (index + 2 < routes.length) {
          const afterNextRoute = routes[index + 2];
          const afterNextStart = moment(afterNextRoute.startDate);
          maxEnd = maxEnd ? moment.min(maxEnd, afterNextStart) : afterNextStart;
        }
        // If no constraints, expand to fill available space
        if (!maxEnd) {
          maxEnd = moment(newNextStart).add(nextRoute.nights, 'days');
        }

        // Calculate new end date maintaining nights, but expand if space available
        const nightsToAdd = nextStart.diff(routeEnd, 'days');
        const expandedNights = nextRoute.nights + (nightsToAdd > 0 ? nightsToAdd : 0);
        const newNextEnd = moment.min(
          moment(newNextStart).add(expandedNights, 'days'),
          maxEnd
        );

        routes[index + 1].startDate = newNextStart.toISOString();
        routes[index + 1].endDate = newNextEnd.toISOString();
        routes[index + 1].nights = moment(newNextEnd).diff(moment(newNextStart), 'days');

        // Ensure minimum 1 night
        if (routes[index + 1].nights < 1) {
          routes[index + 1].nights = 1;
          routes[index + 1].endDate = moment(routes[index + 1].startDate).add(1, 'day').toISOString();
        }
      }
    }

    // Remove any routes that fall outside trip date boundaries
    if (tripStart && tripEnd) {
      // Process in reverse order to safely remove items
      for (let i = routes.length - 1; i >= 0; i--) {
        const currentRoute = routes[i];
        const currentStart = moment(currentRoute.startDate);
        const currentEnd = moment(currentRoute.endDate);

        // Remove route if it's completely outside the trip date range
        if (currentEnd.isSameOrBefore(tripStart, 'day') || currentStart.isSameOrAfter(tripEnd, 'day')) {
          routes.splice(i, 1);
        }
      }
    }
    /* eslint-enable no-param-reassign */
  };

  handleSelection = (index, item) => {
    const { routes, onUpdate } = this.props;
    const modRoutes = [...routes];
    const route = { ...routes[index] };
    route.placeId = item._id || '';
    route.placeName = item.name || ''; // Store display name directly
    route.displayName = item.displayName || '';
    modRoutes[index] = route;
    onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
    // Call getRouteSummary after place selection
    this.setState({ disableCta: true });
    this.debouncedGetRouteSummary(modRoutes);
  };

  handleChange = (index, field, value) => {
    const {
      routes, onUpdate, requirement,
    } = this.props;
    const modRoutes = [...routes];
    const route = { ...routes[index] };
    if (field === 'place') {
      // Store typed value directly and clear placeId when user starts typing
      route.placeName = value;
      route.displayName = value;
      route.placeId = '';
      modRoutes[index] = route;
        // Update routes immediately for place changes (no duration validation needed)
        onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
      // Call getRouteSummary after place change (debounced)
      this.setState({ disableCta: true });
      this.debouncedGetRouteSummary(modRoutes);
      return;
    }
    if (field === 'duration') {
      const newNights = parseInt(value, 10);
      if (Number.isNaN(newNights) || newNights < 1) {
        return;
      }
      route.nights = newNights;
      const { tripDates: { from } = {} } = requirement;
      let lastFrom = from;
      if (index !== 0) {
        lastFrom = routes[index - 1].endDate;
      }
      route.startDate = moment(lastFrom).toISOString();
      route.endDate = moment(lastFrom).add(newNights, 'day').toISOString();
    }
    modRoutes[index] = route;

    // Handle date overlap adjustments
    this.adjustOverlappingDates(modRoutes, index);

    const { error, errorMsg } = this.validateRoutes(modRoutes);
    if (error) {
      this.setState({ errorMsg: errorMsg });
      return;
    }
    onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
    // Call getRouteSummary after duration change
    if (field === 'duration') {
      this.setState({ disableCta: true });
      this.debouncedGetRouteSummary(modRoutes);
    }
  };

  handleDurationChange = (index, action) => {
    const { routes } = this.props;
    const route = { ...routes[index] };
    const newNights = action === 'add' ? route.nights + 1 : route.nights - 1;

    // If new duration is 0, remove the route
    if (newNights === 0) {
      this.handleRouteDelete(index);
      return;
    }

    // Create a temporary route with the new nights value for validation
    const tempRoute = {
      ...route,
      nights: newNights,
      endDate: moment(route.startDate).add(newNights, 'day').toISOString(),
    };
    const tempRoutes = [...routes];
    tempRoutes[index] = tempRoute;

    const { error, errorMsg } = this.validateRoutes(tempRoutes);
    if (!error) {
      this.handleChange(index, 'duration', newNights);
    } else {
      this.setState({ errorMsg: errorMsg });
      setTimeout(() => {
        this.setState({ errorMsg: null });
      }, 1000);
    }
  };

  validateRoutes = (routes) => {
    if (!routes || routes.length === 0) {
      return { error: false, errorMsg: '' };
    }

    const { requirement } = this.props;
    const { tripDates: { from, to } = {} } = requirement;

    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];
      if (!route || !route.startDate || !route.endDate) {
        continue;
      }

      // Calculate nights from startDate and endDate, or use route.nights if available
      const nights = route.nights || moment(route.endDate).diff(moment(route.startDate), 'days');

      if (nights < 1) {
        return {
          error: true,
          errorMsg: 'Duration must be at least 1 day',
        };
      }

      const routeStartDate = moment(route.startDate);
      const routeEndDate = moment(route.endDate);

      // Check if exceeds trip end date
      if (to && routeEndDate.isAfter(moment(to), 'day')) {
        return {
          error: true,
          errorMsg: 'Duration cannot exceed trip end date',
        };
      }

      // Check if exceeds trip start date
      if (from && routeStartDate.isBefore(moment(from), 'day')) {
        return {
          error: true,
          errorMsg: 'Start date cannot be before trip start date',
        };
      }

      // Check overlap with next route
      if (i + 1 < routes.length) {
        const nextRoute = routes[i + 1];
        if (nextRoute && nextRoute.startDate) {
          const nextRouteStart = moment(nextRoute.startDate);
          if (routeEndDate.isAfter(nextRouteStart, 'day')) {
            // This will be handled by adjustOverlappingDates, but we can still warn
            // Actually, we'll let adjustOverlappingDates handle it, so no error here
          }
        }
      }
    }

    return {
      error: false,
      errorMsg: '',
    };
  };

  handleRouteDelete = (index) => {
    const {
      routes, onUpdate, requirement,
    } = this.props;
    const { tripDates: { from } = {} } = requirement;
    const modRoutes = [...routes];
    modRoutes.splice(index, 1);
    if (modRoutes.length === 0 && from) {
      const startDate = moment(from).toISOString();
      const endDate = moment(from).add(1, 'day').toISOString();
      modRoutes.push({
        placeId: '',
        placeName: '',
        displayName: '',
        startDate,
        endDate,
        nights: 1,
      });
    }
    onUpdate({ field: 'routes', value: modRoutes, resetRoutes: false });
    // Call getRouteSummary after route deletion
    this.setState({ disableCta: true });
    this.debouncedGetRouteSummary(modRoutes);
  };

  filterCities = (searchValue) => {
    const { cities = [] } = this.props;
    if (!searchValue) return [];
    const lowerSearch = searchValue.toLowerCase();
    return cities.filter((city) => {
      const name = (city.name || '').toLowerCase();
      const normalizedName = (city.normalizedName || '').toLowerCase();
      return name.includes(lowerSearch) || normalizedName.includes(lowerSearch);
    }).slice(0, 10); // Limit to 10 results
  };

  render() {
    const {
      classes, routes,
      requirement,
      itinerary,
      hasEdits,
      isLoading,
      onSubmitHandler,
      cities = [],
      summary = '',
    } = this.props;
    const {
      errorMsg, isLoadingSuggestions, disableCta,
      isLoadingSummary, summaryFromGetRouteSummary,
    } = this.state;
    return (
      <div className={classes.container}>
        {
          isLoadingSuggestions && (
            <Typography className={classes.loadingText}>
              Loading route suggestions...
            </Typography>
          )
        }
    
        {
          isLoadingSuggestions
            ? (
              <>
                <div className={classes.loader}>
                  <Loader />
                </div>
              </>
            )
            : (
              isLoadingSummary
                ? (
                  <div className={classes.loader}>
                    <Loader />
                  </div>
                ) : (
                  <div className={classes.summaryContainer}>
                    <Typography className={classes.summaryText}>
                      {summaryFromGetRouteSummary ? '' : <b>Suggested Route:</b>} {summary}
                    </Typography>
                  </div>
                )
            )
        }
        {!isLoadingSuggestions && (
          <div className={classes.routesContainer}>
            {routes.map((route, index) => {
              const { startDate, endDate, nights } = route;
              const { tripDates: { from, to } = {} } = requirement;
              let dateStr = '';
              if (startDate && endDate) {
                const fromDate = moment(startDate);
                const toDate = moment(endDate);
                dateStr = `${fromDate.format('DD MMM')} - ${toDate.format('DD MMM')} | ${nights} N`;
              }
              let disableFromDate = moment().toDate();
              if (index !== 0) {
                disableFromDate = moment(routes[index - 1].endDate).toDate();
              }
              // Check if route start date is same as trip start date (from)
              const isStartDateSameAsFrom = from && startDate
                ? moment(startDate).isSame(moment(from), 'day')
                : false;
              // Check if route end date is same as trip end date (to)
              const isEndDateSameAsTo = to && endDate
                ? moment(endDate).isSame(moment(to), 'day')
                : false;
              // Use placeName directly (either typed or selected name)
              const placeDisplayValue = route.displayName || '';
              // Filter cities based on typed value
              // CRITICAL: Always provide cities data so dropdown can show immediately when field is focused
              // The Autocomplete component only shows dropdown when isVisible=true (on focus),
              // so providing data always is safe - dropdown won't show unexpectedly
              const allCities = cities || [];
              // When user is actively typing (has value and it's not just the selected value), filter cities
              // Otherwise always show first 10 cities so dropdown can appear immediately when clicked
              // This ensures data is available when onFocusHandler sets isVisible=true
              const isTyping = placeDisplayValue && placeDisplayValue.length > 0 && !route.placeId;
              const filteredCities = isTyping
                ? this.filterCities(placeDisplayValue)
                : allCities.slice(0, 10); // Always show first 10 cities as fallback

              // Create a unique key from route properties
              // Use index only to ensure component doesn't remount when placeId changes
              // This preserves Autocomplete's internal state (isVisible, focused) when field is clicked
              const routeKey = `route-${index}`;
              return (
                <div key={routeKey} className={classes.routeRow}>
                  <div className={classes.routeCircle} />
                  <Autocomplete
                    accessor="displayName"
                    placeholder="City"
                    id="_id"
                    value={placeDisplayValue}
                    bodyClass={classes.labelClass}
                    extraClass={classes.inputContainer}
                    inputClass={classes.autoCompleteInput}
                    data={filteredCities}
                    onChange={(value) => this.handleChange(index, 'place', value)}
                    onSelected={(item) => this.handleSelection(index, item)}
                  />
                  <div className={classes.durationSection}>
                    <Typography className={classes.duration}>
                      Duration:
                    </Typography>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => this.handleDurationChange(index, 'subtract')}
                    >
                      <MinusIcon className={classes.icon} />
                    </IconButton>
                    <InputBase
                      value={nights}
                      className={classes.durationText}
                      type="number"
                      min="1"
                      classes={{
                        input: classes.durationInput,
                      }}
                      onChange={(e) => this.handleChange(index, 'duration', e.target.value)}
                    />
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => this.handleDurationChange(index, 'add')}
                    >
                      <AddIcon className={classes.icon} />
                    </IconButton>
                  </div>
                  <DateRangeSelection
                    extraClass={classes.dateSelection}
                    popupClass={classes.datePopup}
                    value={dateStr}
                    dates={{
                      from: startDate ? moment(startDate).toDate() : null,
                      to: endDate ? moment(endDate).toDate() : null,
                    }}
                    shouldDisableDate={this.disabledDateFn}
                    disableFromDate={disableFromDate}
                    placeholder="Dates"
                    onSelected={(date) => this.handleDateSelection(index, date)}
                  />
                  <IconButton
                    onClick={() => this.handleRouteDelete(index)}
                    className={classes.deleteButton}
                  >
                    <DeleteIcon className={classes.deleteIcon} />
                  </IconButton>
                </div>
              );
            })}
          </div>
        )}
        <Footer extraClass={classes.footerFlexEnd}>
          {!isLoadingSuggestions && (
            <Button
              onClick={this.handleItemAddition}
              variant="outlined"
              className={classes.addButton}
            >
              Add city
            </Button>
          )}
          {errorMsg && (
            <Typography className={classes.errorMsg}>{errorMsg}</Typography>
          )}
          {itinerary && hasEdits && (
            <Button
              loading={isLoading}
              onClick={() => {
                // Set flag to trigger chatAgent API call
                window.localStorage.setItem('shouldCallChatAgent', 'true');
                // Call the edit to create a new itinerary draft
                onSubmitHandler({ ignoreTemplate: true, isV3: true, cloneItinerary: true });
              }}
              className={classes.submitButton}
              disabled={errorMsg || isLoadingSuggestions || disableCta}
            >
              BUILD NEW WITH AI
            </Button>
          )}
          <Button
            loading={isLoading}
            onClick={() => {
              // Set flag to trigger chatAgent API call
              window.localStorage.setItem('shouldCallChatAgent', 'true');
              // Call the submitHandler to create the itinerary draft
              onSubmitHandler({ ignoreTemplate: true, isV3: true }); // true = ignoreTemplate, always create from scratch for AI
            }}
            className={classes.submitButton}
            disabled={errorMsg || isLoadingSuggestions || disableCta}
          >
            {
              itinerary
                ? (hasEdits ? 'UPDATE OLD WITH AI' : 'EDIT WITH AI')
                : 'BUILD WITH AI'
            }
            {
              disableCta ? '...' : ''
            }
          </Button>
        </Footer>
      </div>
    );
  }
}

RoutePlan.propTypes = {
  classes: PropTypes.object,
  routes: PropTypes.array,
  cities: PropTypes.array,
  requirement: PropTypes.object.isRequired,
  errorMsg: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  summary: PropTypes.string,
  itinerary: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  hasEdits: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSubmitHandler: PropTypes.func.isRequired,
};

const styles = (theme) => ({
  container: {
    padding: '20px 40px',
  },
  routesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '10px 30px',
  },
  routeRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 16,
    position: 'relative',
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      left: -22,
      top: 30,
      height: 36,
      width: 4,
      backgroundColor: 'rgba(0, 144, 181, 0.4)',
    },
  },
  routeCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: `3px solid rgba(0, 144, 181, 0.4)`,
    backgroundColor: 'transparent',
    position: 'absolute',
    left: -30,
    top: '50%',
    transform: 'translateY(-50%)',
    flexShrink: 0,
    zIndex: 10,
  },
  inputContainer: {
    height: 40,
    flex: 1,
    width: 500,
    paddingRight: 10,
    borderRadius: 4,
  },
  labelClass: {
    borderRadius: 4,
    border: `1px solid ${theme.colors.border}`,
  },
  autoCompleteInput: {
    padding: '6px 5px 6px 15px',
    height: '100%',
    width: '100%',
  },
  durationSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  duration: {
    fontSize: 12,
    color: theme.colors.textLight,
    padding: '0 10px',
  },
  durationText: {
    color: theme.colors.black,
    fontSize: 14,
    width: 40,
  },
  durationInput: {
    textAlign: 'center',
  },
  iconButton: {
    padding: 4,
  },
  icon: {
    width: 16,
    height: 16,
    color: theme.colors.primary,
  },
  dateSelection: {
    flex: 1,
    marginLeft: 10,
  },
  datePopup: {
    left: -210,
  },
  addButton: {
    width: 120,
    marginRight: 'auto',
    borderRadius: 16,
  },
  deleteButton: {
    marginLeft: 10,
    padding: 4,
  },
  deleteIcon: {
    width: 20,
    height: 20,
    color: theme.colors.red,
  },
  footerFlexEnd: {
    padding: '20px 0 10px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
  },
  errorMsg: {
    padding: '0 16px',
    color: theme.colors.red,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.5,
    display: 'flex',
    alignItems: 'center',
  },
  submitButton: {
  },
  summaryContainer: {
    marginBottom: 20,
  },
  summaryText: {
    fontSize: 14,
    color: theme.colors.text || '#333',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  loadingText: {
    fontSize: 18,
    color: theme.colors.textLight || '#666',
    marginBottom: 10,
    fontWeight: 'bold',
  },
  loader: {
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }
});

export default withStyles(styles)(RoutePlan);
